
import Vue from "vue";
import { View } from "@/models/view";
import { Browse } from "@/models/browse";
import { BrowseService } from "@/services/browse-service";
import { ViewSidebar, ViewSidebarBlock, VIEW_SIDEBAR_FIELDS_BLOCK } from "@/utils/views/view-sidebar";
import { getSidebarBlockDefinition, getSidebarBlockName, ISidebarTemplate } from "@/utils/views/sidebar-blocks";
import { ViewService } from "@/services/view-service";
import { AuthService } from "@/services/auth-service";
import { ISelectItem } from "winfakt-vue-components";
import AppSidebarAddBlockModal from "./SidebarAddBlockModal.vue";
import AppSidebarBlockEdit from "./SidebarBlockEdit.vue";
import AppSidebarFieldEdit from "./SidebarFieldEdit.vue";
import { ViewColumn } from "@/utils/views/view-column";

export default Vue.extend({
	data(){
		return {
			showAddBlock: false,
			editingBlock: null as ViewSidebarBlock | null,
			editingIndex: -1,
			editingIsNew: false,
			browse: null as Browse | null,
			showCopy: false,
			copyTargets: [] as number[],
			copying: false,
			editingHeaderField: null as ViewColumn | null,
			editingHeaderTarget: "title" as "title" | "subtitle",
			editingHeaderIndex: -1
		};
	},
	props: {
		view: {
			type: Object as () => View,
			required: true
		}
	},
	computed: {
		sidebar():ViewSidebar {
			return this.view.Sidebar;
		},
		triggerItems():ISelectItem<string>[] {
			return [
				{text: this.$ct("sidebar.trigger-select").toString(), value: "select"},
				{text: this.$ct("sidebar.trigger-double-click").toString(), value: "double-click"}
			];
		},
		otherViews():View[] {
			if (!this.browse) return [];
			return this.browse.Views.filter(v=>v.ID != this.view.ID && v.UserID == AuthService.wfUser.ID);
		},
		positionItems():ISelectItem<string>[] {
			return [
				{text: this.$ct("sidebar.position-right").toString(), value: "right"},
				{text: this.$ct("sidebar.position-left").toString(), value: "left"}
			];
		}
	},
	methods: {
		getBlockIcon(block:ViewSidebarBlock):string {
			if (block.isFieldsBlock) return "th-list";
			let def = getSidebarBlockDefinition(block.Type);
			return def ? def.icon : "question";
		},
		getBlockSubtext(block:ViewSidebarBlock):string {
			if (block.isFieldsBlock) {
				return this.$ctc("sidebar.fields-block-count", block.Fields.length, {count: block.Fields.length}).toString();
			}
			if (!block.Title) return "";
			return getSidebarBlockName(block.Type);
		},
		getBlockTitle(block:ViewSidebarBlock):string {
			if (block.Title) return block.Title;
			if (block.isFieldsBlock) return this.$ct("sidebar.fields-block").toString();
			return getSidebarBlockName(block.Type);
		},
		addBlock(type:string){
			this.showAddBlock = false;
			let block = new ViewSidebarBlock();
			block.Type = type;
			if (type == VIEW_SIDEBAR_FIELDS_BLOCK) {
				this.editingBlock = block;
				this.editingIndex = -1;
				this.editingIsNew = true;
				return;
			}
			this.sidebar.Blocks.push(block);
		},
		addTemplate(template:ISidebarTemplate){
			this.showAddBlock = false;
			let existing = this.sidebar.Blocks.map(b=>b.Type);
			for (let type of template.blocks) {
				if (existing.indexOf(type) != -1) continue;
				let block = new ViewSidebarBlock();
				block.Type = type;
				this.sidebar.Blocks.push(block);
			}
			this.sidebar.Enabled = true;
		},
		toggleCopyTarget(id:number){
			let index = this.copyTargets.indexOf(id);
			if (index == -1) {
				this.copyTargets.push(id);
			} else {
				this.copyTargets.splice(index, 1);
			}
		},
		async copyToViews(){
			let targets = this.otherViews.filter(v=>this.copyTargets.indexOf(v.ID) != -1);
			if (targets.length == 0) return;
			this.copying = true;
			try {
				for (let target of targets) {
					target.Sidebar = new ViewSidebar(this.sidebar.getJSON());
				}
				await ViewService.updateViews(targets);
				this.$wf.notify(this.$ct("sidebar.copied-to-views", {count: targets.length}).toString(), "success", 3000, "check");
				this.showCopy = false;
				this.copyTargets = [];
			} finally {
				this.copying = false;
			}
		},
		editBlock(block:ViewSidebarBlock){
			this.editingBlock = new ViewSidebarBlock(block);
			this.editingIndex = this.sidebar.Blocks.indexOf(block);
			this.editingIsNew = false;
		},
		saveBlock(){
			if (!this.editingBlock) return;
			if (this.editingIndex == -1) {
				this.sidebar.Blocks.push(this.editingBlock);
			} else {
				this.sidebar.Blocks.splice(this.editingIndex, 1, this.editingBlock);
			}
			this.cancelEdit();
		},
		cancelEdit(){
			this.editingBlock = null;
			this.editingIndex = -1;
			this.editingIsNew = false;
		},
		removeBlock(index:number){
			this.sidebar.Blocks.splice(index, 1);
		},
		getFieldFriendlyName(field:ViewColumn):string {
			if (!this.browse) return "";
			let namedProp = this.browse.type.getField(field.Property);
			return namedProp ? namedProp.getPropertyTranslation() : "ERROR";
		},
		editHeaderField(target:"title" | "subtitle", field:ViewColumn | null, index:number){
			this.editingHeaderTarget = target;
			this.editingHeaderIndex = index;
			this.editingHeaderField = field ? new ViewColumn(field) : new ViewColumn();
		},
		saveHeaderField(){
			if (!this.editingHeaderField) return;
			if (this.editingHeaderTarget == "title") {
				this.sidebar.TitleField = this.editingHeaderField;
			} else if (this.editingHeaderIndex == -1) {
				this.sidebar.SubtitleFields.push(this.editingHeaderField);
			} else {
				this.sidebar.SubtitleFields.splice(this.editingHeaderIndex, 1, this.editingHeaderField);
			}
			this.editingHeaderField = null;
		}
	},
	async created(){
		this.browse = (await BrowseService.getBrowse(this.view.BrowseID)).browse;
	},
	components: {
		AppSidebarAddBlockModal,
		AppSidebarBlockEdit,
		AppSidebarFieldEdit
	},
	i18nEx: {
		componentPrefix: "components.view-editor"
	}
});
